import type { NavMeConfig, MultisetMapType } from '../types';

/**
 * Credential/config handling.
 *
 * Precedence: localStorage (setup screen) > Vite env defaults.
 *
 * SECURITY NOTES (production hardening):
 * - The MultiSet WebXR SDK makes direct browser->API calls; the platform's
 *   protection model is domain whitelisting (dashboard: Credentials ->
 *   Settings -> Domains). Still, anything shipped to the browser is readable.
 * - Credentials persist in localStorage for testing convenience (enter once
 *   per device); they are never rendered back into the UI after saving.
 * - TODO(production): switch back to sessionStorage or gate behind staff
 *   login if devices are shared/public.
 * - TODO(production): replace direct clientSecret entry with a server-side
 *   token exchange endpoint if/when MultiSet exposes short-lived tokens for
 *   the SDK, or gate the setup screen behind staff auth.
 */

const STORAGE_KEY = 'navme.config.v1';

function envDefault(): NavMeConfig {
  const env = import.meta.env;
  return {
    clientId: (env.VITE_MULTISET_CLIENT_ID as string) || '',
    clientSecret: (env.VITE_MULTISET_CLIENT_SECRET as string) || '',
    mapType: ((env.VITE_MULTISET_MAP_TYPE as string) || 'map') as MultisetMapType,
    code: (env.VITE_MULTISET_CODE as string) || '',
    // Compass-mode calibration — see the TODO on NavMeConfig. Uncalibrated (0) by default.
    mapNorthOffsetDeg: Number(env.VITE_MULTISET_MAP_NORTH_OFFSET_DEG as string) || 0,
    showLocalizationMesh: false,
  };
}

export function loadConfig(): NavMeConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const stored = JSON.parse(raw) as Partial<NavMeConfig>;
      const base = envDefault();
      return {
        clientId: stored.clientId || base.clientId,
        clientSecret: stored.clientSecret || base.clientSecret,
        mapType: (stored.mapType as MultisetMapType) || base.mapType,
        code: stored.code || base.code,
        mapNorthOffsetDeg: stored.mapNorthOffsetDeg ?? base.mapNorthOffsetDeg,
        showLocalizationMesh: stored.showLocalizationMesh ?? base.showLocalizationMesh,
      };
    }
  } catch {
    /* corrupted storage — fall through to env defaults */
  }
  return envDefault();
}

export function saveConfig(config: NavMeConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

export function clearConfig(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function isConfigComplete(c: NavMeConfig): boolean {
  return Boolean(c.clientId && c.clientSecret && c.code);
}

/** Masked view for display — secrets are never shown after saving. */
export function maskedSummary(c: NavMeConfig): string {
  const mask = (s: string) => (s ? `${s.slice(0, 3)}•••` : '(not set)');
  return `ID ${mask(c.clientId)} · ${c.mapType} ${c.code || '(not set)'}`;
}
